import React, { useState } from 'react';
import { Image, Copy, Trash2, Sparkles } from 'lucide-react';

const AltTextGeneratorTool = () => {
  const [imageDescription, setImageDescription] = useState('');
  const [context, setContext] = useState('');
  const [imageType, setImageType] = useState('photo');
  const [generatedAlts, setGeneratedAlts] = useState([]);
  const [copied, setCopied] = useState('');

  const generateAltTexts = () => {
    if (!imageDescription.trim()) return;

    const desc = imageDescription.trim();
    const ctx = context.trim();
    
    const alts = [];

    // Basic descriptive
    alts.push({
      type: 'Descriptive',
      text: desc,
      length: desc.length,
      tip: 'Direct description of the image'
    });

    // With context
    if (ctx) {
      alts.push({
        type: 'Contextual',
        text: `${desc} - ${ctx}`,
        length: (`${desc} - ${ctx}`).length,
        tip: 'Includes page context'
      });
    }

    // Action-oriented
    if (imageType === 'photo') {
      alts.push({
        type: 'Action-oriented',
        text: `Image showing ${desc.toLowerCase()}`,
        length: (`Image showing ${desc.toLowerCase()}`).length,
        tip: 'Good for informative images'
      });
    }

    // Product style
    if (imageType === 'product') {
      alts.push({
        type: 'Product',
        text: `${desc} - product image`,
        length: (`${desc} - product image`).length,
        tip: 'For e-commerce products'
      });
      alts.push({
        type: 'Detailed Product',
        text: `${desc} available for purchase`,
        length: (`${desc} available for purchase`).length,
        tip: 'Encourages action'
      });
    }

    // Infographic style
    if (imageType === 'infographic') {
      alts.push({
        type: 'Infographic',
        text: `Infographic explaining ${desc.toLowerCase()}`,
        length: (`Infographic explaining ${desc.toLowerCase()}`).length,
        tip: 'For data visualizations'
      });
    }

    // Screenshot style
    if (imageType === 'screenshot') {
      alts.push({
        type: 'Screenshot',
        text: `Screenshot of ${desc.toLowerCase()}`,
        length: (`Screenshot of ${desc.toLowerCase()}`).length,
        tip: 'For UI/software screenshots'
      });
    }

    // SEO optimized (with keyword if context provided)
    if (ctx) {
      alts.push({
        type: 'SEO Optimized',
        text: `${ctx}: ${desc}`,
        length: (`${ctx}: ${desc}`).length,
        tip: 'Keyword-focused for SEO'
      });
    }

    // Concise version
    const words = desc.split(' ');
    if (words.length > 5) {
      const concise = words.slice(0, 5).join(' ');
      alts.push({
        type: 'Concise',
        text: concise,
        length: concise.length,
        tip: 'Shortened version'
      });
    }

    setGeneratedAlts(alts);
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopied(index);
    setTimeout(() => setCopied(''), 2000);
  };

  const getLengthColor = (length) => {
    if (length <= 125) return 'text-green-400';
    if (length <= 150) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
        <p className="text-sm text-blue-300">
          Good alt text should be descriptive, concise (under 125 characters), and contextually relevant.
          Avoid starting with "Image of..." unless necessary.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Image Description</label>
        <input
          type="text"
          value={imageDescription}
          onChange={(e) => setImageDescription(e.target.value)}
          placeholder="Describe what's in the image..."
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-blue-500"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Page Context / Keyword (optional)</label>
          <input
            type="text"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="e.g., SEO tools, recipe, tutorial"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Image Type</label>
          <select
            value={imageType}
            onChange={(e) => setImageType(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          >
            <option value="photo">Photo</option>
            <option value="product">Product Image</option>
            <option value="infographic">Infographic</option>
            <option value="screenshot">Screenshot</option>
            <option value="illustration">Illustration</option>
            <option value="icon">Icon</option>
          </select>
        </div>
      </div>

      <button
        onClick={generateAltTexts}
        disabled={!imageDescription.trim()}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
      >
        <Sparkles size={18} />
        Generate Alt Texts
      </button>

      {generatedAlts.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-gray-300">Generated Alt Texts</h3>
          {generatedAlts.map((alt, i) => (
            <div key={i} className="p-4 bg-gray-800 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-blue-400 font-medium">{alt.type}</span>
                <span className={`text-xs ${getLengthColor(alt.length)}`}>
                  {alt.length} chars
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white flex-1 mr-4">{alt.text}</span>
                <button
                  onClick={() => copyToClipboard(alt.text, i)}
                  className={`p-2 rounded transition-colors ${
                    copied === i ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  <Copy size={16} />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">{alt.tip}</p>
            </div>
          ))}
        </div>
      )}

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Alt Text Best Practices</h3>
        <ul className="text-sm text-gray-400 space-y-2">
          <li>• <span className="text-green-400">Do:</span> Be specific and descriptive</li>
          <li>• <span className="text-green-400">Do:</span> Keep it under 125 characters</li>
          <li>• <span className="text-green-400">Do:</span> Include relevant keywords naturally</li>
          <li>• <span className="text-red-400">Don't:</span> Start with "Image of" or "Picture of"</li>
          <li>• <span className="text-red-400">Don't:</span> Stuff keywords unnecessarily</li>
          <li>• <span className="text-red-400">Don't:</span> Leave decorative images with alt text (use alt="")</li>
        </ul>
      </div>

      <button
        onClick={() => { setImageDescription(''); setContext(''); setGeneratedAlts([]); }}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
      >
        <Trash2 size={16} />
        Clear
      </button>
    </div>
  );
};

export default AltTextGeneratorTool;
